import React, { useMemo } from 'react';
import { Button, Form, Input, Segmented, Space, message } from 'antd';
import moment from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import RuleTimeWindowPicker from '@src/components/common/RuleTimeWindowPicker';
import {
  createAlertRule,
  updateAlertRule,
  AlertRulePayload,
  RuleChannelPayload,
} from '@src/api/alertRules';
import { ALERT_RULES_QUERY_KEY } from '../list/hooks';
import ConditionBuilder from './ConditionBuilder';
import ChannelAttach from './ChannelAttach';
import { AlertRule, DraftChannel, DraftCondition } from '../types';
interface RuleFormProps {
  onClose: () => void;
  initialValues?: AlertRule;
}

interface FormValues {
  name: string;
  ruleType: 'allow' | 'deny';
  window: [moment.Moment, moment.Moment];
  conditions: DraftCondition[];
  channels: DraftChannel[];
}

const toPayload = (values: FormValues): AlertRulePayload => {
  const [from, to] = values.window;
  const conditions = (values.conditions || []).map((c) => {
    if (c.type !== 'dimension') {
      return { type: 'metric' as const, metricKey: c.metricKey };
    }
    if (c.matchMode === 'dimension_equals' || c.matchMode === 'dimension_contains') {
      return {
        type: 'dimension' as const,
        matchMode: c.matchMode,
        dimensionKey: c.dimensionKey,
        dimensionValue: c.dimensionValue,
        dimensionLabel: c.dimensionLabel ?? null,
      };
    }
    return { type: 'dimension' as const, matchMode: c.matchMode, dimensionKey: c.dimensionKey };
  });
  const channels: RuleChannelPayload[] = [];
  (values.channels || []).forEach((c) => {
    if (!c.channelType) return;
    if (c.channelType === 'email') {
      // Expand the multiselect recipients into one channel attachment per email.
      const emails = (c.targets?.length ? c.targets : c.target ? [c.target] : [])
        .map((e) => e.trim())
        .filter(Boolean);
      [...new Set(emails)].forEach((email) => {
        channels.push({ channelType: 'email', target: email, configId: null });
      });
      return;
    }
    // Expand the multiselect registrations into one channel attachment per id.
    const ids = c.configIds?.length ? c.configIds : c.configId != null ? [c.configId] : [];
    [...new Set(ids)].forEach((configId) => {
      channels.push({ channelType: c.channelType, target: null, configId });
    });
  });
  return {
    name: values.name.trim(),
    ruleType: values.ruleType,
    activeFrom: from.toISOString(),
    activeUntil: to.toISOString(),
    conditions,
    channels,
  };
};

const RuleForm = ({ onClose, initialValues }: RuleFormProps) => {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const isEditing = !!initialValues;

  const initial: FormValues = useMemo(() => {
    if (initialValues) {
      const conditions: DraftCondition[] = (initialValues.conditions || []).map((c) =>
        c.conditionType === 'dimension'
          ? {
              type: 'dimension',
              matchMode: (c.matchMode as any) || 'has_dimension',
              dimensionKey: c.dimensionKey || '',
              dimensionValue: c.dimensionValue ?? undefined,
              dimensionLabel: c.dimensionLabel ?? undefined,
            }
          : { type: 'metric', metricKey: c.metricKey || '' },
      );
      const channels: DraftChannel[] = (initialValues.channels || []).map((c) => ({
        channelType: c.channelType,
        target: c.target,
        targets: c.channelType === 'email' && c.target ? [c.target] : undefined,
        configId: c.configId,
        configIds: c.channelType !== 'email' && c.configId != null ? [c.configId] : undefined,
      }));
      return {
        name: initialValues.name,
        ruleType: initialValues.ruleType,
        window: [moment(initialValues.activeFrom), moment(initialValues.activeUntil)],
        conditions,
        channels,
      };
    }
    return {
      name: '',
      ruleType: 'deny',
      window: [moment(), moment().add(4, 'hour')],
      conditions: [],
      channels: [{ channelType: 'email', target: '' }],
    };
  }, [initialValues]);

  const onDone = (verb: string) => {
    queryClient.invalidateQueries([ALERT_RULES_QUERY_KEY]);
    message.success(`Rule ${verb} successfully.`);
    onClose();
  };

  const { mutate: submitCreate, isLoading: isCreating } = useMutation(
    (values: FormValues) => createAlertRule(toPayload(values)),
    {
      onSuccess: () => onDone('created'),
      onError: (err: any) =>
        message.error(err?.response?.data?.error || 'Failed to create rule. Please try again.'),
    },
  );

  const { mutate: submitUpdate, isLoading: isUpdating } = useMutation(
    (values: FormValues) => updateAlertRule(initialValues!.id, toPayload(values)),
    {
      onSuccess: () => onDone('updated'),
      onError: (err: any) =>
        message.error(err?.response?.data?.error || 'Failed to update rule. Please try again.'),
    },
  );

  const handleFinish = (values: FormValues) => {
    if (isEditing) submitUpdate(values);
    else submitCreate(values);
  };

  const ruleType = Form.useWatch('ruleType', form);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={initial}
      style={{ marginTop: 16, maxWidth: 760 }}
    >
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: 'A rule name is required' }]}
      >
        <Input placeholder="e.g. Suppress CDN maintenance alerts" maxLength={255} />
      </Form.Item>

      <Form.Item
        name="window"
        rules={[
          {
            validator: (_, val: [moment.Moment, moment.Moment]) => {
              if (!val || !val[0] || !val[1]) return Promise.reject('A time window is required');
              if (!val[1].isAfter(val[0])) return Promise.reject('End must be after start');
              return Promise.resolve();
            },
          },
        ]}
      >
        <RuleTimeWindowPicker />
      </Form.Item>

      <div
        style={{
          border: '2px solid #d9d9d9',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          maxWidth: 720,
        }}
      >
        <Form.Item label="Rule Type" name="ruleType">
          <Segmented
            options={[
              { label: 'Mute Specific', value: 'deny' },
              { label: 'Mute everything except', value: 'allow' },
            ]}
          />
        </Form.Item>
        <div style={{ color: '#595959', fontSize: 13, marginTop: -12, marginBottom: 16 }}>
          {ruleType === 'allow'
            ? 'Mute all AI alerts except the ones that match the following rule'
            : 'Mute AI Alerts that has the following rule'}
        </div>

        <Form.Item
          name="conditions"
          validateTrigger="onSubmit"
          style={{ marginBottom: 0 }}
          rules={[
            {
              validator: (_, val: DraftCondition[]) => {
                const hasDim = (val || []).some(
                  (c) => c.type === 'dimension' && (c as any).dimensionKey,
                );
                const hasMetric = (val || []).some(
                  (c) => c.type === 'metric' && (c as any).metricKey,
                );
                if (!hasDim && !hasMetric) {
                  return Promise.reject('Select at least one dimension or metric');
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <ConditionBuilder />
        </Form.Item>
      </div>

      <div style={{ fontWeight: 500, marginBottom: 12 }}>
        The rule is applicable to the following channels
      </div>

      <Form.Item
        name="channels"
        validateTrigger="onSubmit"
        rules={[
          {
            validator: (_, val: DraftChannel[]) => {
              const valid = (val || []).filter(
                (c) =>
                  c.channelType &&
                  (c.targets?.length || c.target?.trim() || c.configIds?.length || c.configId),
              );
              if (!valid.length) return Promise.reject('Attach at least one channel with a target');
              return Promise.resolve();
            },
          },
        ]}
      >
        <ChannelAttach />
      </Form.Item>

      <Form.Item style={{ marginTop: 16 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={isEditing ? isUpdating : isCreating}>
            {isEditing ? 'Save Changes' : 'Create Rule'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default RuleForm;
